import { PROVIDER_LICENSE_TYPE_OPTIONS } from '@utils/providerLicenseTypes';

import type { GeneratedIntakeField, IntakeFieldValue } from './types';

const SERVICE_MODALITY_FIELD_KEY = 'service_modality';
const COVERAGE_TYPE_FIELD_KEY = 'coverage_type';
const LICENSE_TYPE_FIELD_KEY = 'license_type';
const TAXONOMY_CODE_FIELD_KEY = 'taxonomy_code';

/**
 * The catalog ships bare codes as `license_type` options, matching what
 * document extraction and NPPES lookups write, so the stored value stays a
 * code and only the display text is expanded. Derived from the canonical
 * provider-licence menu so the two cannot drift.
 */
const LICENSE_TYPE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  PROVIDER_LICENSE_TYPE_OPTIONS.map(option => [option.value, option.label]),
);

/**
 * The curated behavioral-health subset of the NUCC taxonomy the catalog ships
 * as `taxonomy_code` options. Codes not in this map render as themselves.
 */
const TAXONOMY_CODE_LABELS: Readonly<Record<string, string>> = {
  '2084P0800X': 'Psychiatry',
  '2084P0802X': 'Addiction Psychiatry',
  '2084P0804X': 'Child & Adolescent Psychiatry',
  '2084P0805X': 'Forensic Psychiatry',
  '103T00000X': 'Psychologist',
  '103TC0700X': 'Clinical Psychologist',
  '103TA0400X': 'Clinical Child & Adolescent Psychologist',
  '103TB0200X': 'Cognitive & Behavioral Psychologist',
  '103TF0000X': 'Family Psychologist',
  '103TH0100X': 'Health Psychologist',
  '102L00000X': 'Psychoanalyst',
  '101Y00000X': 'Counselor',
  '101YM0800X': 'Mental Health Counselor',
  '101YP2500X': 'Professional Counselor',
  '101YA0400X': 'Addiction (Substance Use Disorder) Counselor',
  '104100000X': 'Social Worker',
  '1041C0700X': 'Clinical Social Worker',
  '106H00000X': 'Marriage & Family Therapist',
  '363LP0808X': 'Psychiatric/Mental Health Nurse Practitioner',
  '364SP0808X': 'Psychiatric/Mental Health Clinical Nurse Specialist',
  '363L00000X': 'Nurse Practitioner',
  '363LF0000X': 'Family Nurse Practitioner',
  '363LA2100X': 'Adult Nurse Practitioner',
  '363A00000X': 'Physician Assistant',
  '103K00000X': 'Behavior Analyst',
  '207Q00000X': 'Family Medicine',
  '207R00000X': 'Internal Medicine',
};

const COVERAGE_TYPE_LABELS: Readonly<Record<string, string>> = {
  claims_made: 'Claims made',
  occurrence: 'Occurrence',
};

const SERVICE_MODALITY_LABELS: Readonly<Record<string, string>> = {
  in_person_only: 'In person only',
  telehealth_and_in_person: 'Telehealth and in person',
  telehealth_only: 'Telehealth only',
};

function isServiceModalityField(field: GeneratedIntakeField): boolean {
  return (
    field.field_key === SERVICE_MODALITY_FIELD_KEY ||
    field.destination.field_key === SERVICE_MODALITY_FIELD_KEY
  );
}

function isCoverageTypeField(field: GeneratedIntakeField): boolean {
  return (
    field.field_key === COVERAGE_TYPE_FIELD_KEY ||
    field.destination.field_key === COVERAGE_TYPE_FIELD_KEY
  );
}

function matchesFieldKey(field: GeneratedIntakeField, fieldKey: string): boolean {
  return field.field_key === fieldKey || field.destination.field_key === fieldKey;
}

function readableLegacyValue(value: string): string {
  const words = value.replace(/_+/g, ' ').trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : value;
}

function displayStringValue(field: GeneratedIntakeField, value: string): string {
  if (isServiceModalityField(field)) {
    return SERVICE_MODALITY_LABELS[value] ?? readableLegacyValue(value);
  }
  if (isCoverageTypeField(field)) {
    return COVERAGE_TYPE_LABELS[value] ?? readableLegacyValue(value);
  }
  // Licence types and taxonomy codes fall back to the raw code, never to
  // `readableLegacyValue` — it would mangle `2084P0800X` into `2084p0800x`.
  if (matchesFieldKey(field, LICENSE_TYPE_FIELD_KEY)) {
    return LICENSE_TYPE_LABELS[value] ?? value;
  }
  if (matchesFieldKey(field, TAXONOMY_CODE_FIELD_KEY)) {
    // Keep the NUCC code visible: providers verify against the code, and a
    // catalog code missing from this hand-curated map renders as the bare
    // code rather than disappearing.
    const label = TAXONOMY_CODE_LABELS[value];
    return label ? `${value} — ${label}` : value;
  }
  return value;
}

export function displayIntakeFieldValue(
  field: GeneratedIntakeField,
  value: IntakeFieldValue,
): string {
  if (Array.isArray(value)) return value.map(entry => displayStringValue(field, entry)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return displayStringValue(field, value);
  return String(value);
}
